import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Layout from "@/components/Layout";
import JobTimeline from "@/components/JobTimeline";
import DesignFileViewer from "@/components/DesignFileViewer";
import AdditionalAssetsPanel from "@/components/AdditionalAssetsPanel";
import AssetUploader from "@/components/AssetUploader";
import QuoteDraftPanel from "@/components/QuoteDraftPanel";
import MessageAttachment from "@/components/MessageAttachment";
import api from "@/lib/api";
import { toast } from "sonner";
import { PaperPlaneRight, UploadSimple, Truck, Globe, CheckCircle, Trash, ArrowCounterClockwise, DownloadSimple, ChatCircle } from "@phosphor-icons/react";

export default function AdminJobDetail() {
  const { id } = useParams();
  const [job, setJob] = useState(null);
  const [selectedIter, setSelectedIter] = useState(null);
  const [assetMeta, setAssetMeta] = useState({}); // { fid: { original_filename, size } }
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [msgAssets, setMsgAssets] = useState([]);

  const [quoteUrl, setQuoteUrl] = useState("");
  const [quoteNotes, setQuoteNotes] = useState("");
  const [bpEdited, setBpEdited] = useState(false);
  const [designFiles, setDesignFiles] = useState([]);
  const [designNotes, setDesignNotes] = useState("");
  const [finalProofFiles, setFinalProofFiles] = useState([]);
  const [msgText, setMsgText] = useState("");
  const [tracking, setTracking] = useState("");
  const [liveUrl, setLiveUrl] = useState("");
  const [shipNotes, setShipNotes] = useState("");

  const load = async () => {
    const { data } = await api.get(`/jobs/${id}`);
    setJob(data);
    setQuoteUrl(data.quote?.quote_url || "");
    setQuoteNotes(data.quote?.notes || "");
    setBpEdited(!!data.quote?.bp_proposal_edited);
    setTracking(data.shipping?.tracking_url || "");
    setLiveUrl(data.shipping?.live_url || "");
    // Prefer the latest non-deleted iteration; fall back to any if all deleted
    const designs = data.designs || [];
    const liveDesigns = designs.filter((d) => !d.is_deleted);
    const preferred = (liveDesigns.length ? liveDesigns : designs).slice(-1)[0];
    if (preferred) setSelectedIter(preferred.id);
  };
  useEffect(() => { load(); }, [id]);

  // Auto-mark messages as read whenever the job renders with an unread count.
  useEffect(() => {
    if (job && (job.unread_message_count || 0) > 0) {
      api.post(`/jobs/${id}/messages/read`).catch(() => {});
    }
  }, [id, job?.unread_message_count]);

  // Load real filenames for each asset so the list matches the ZIP filenames
  useEffect(() => {
    const ids = job?.asset_ids || [];
    if (!ids.length) return;
    const missing = ids.filter((fid) => !assetMeta[fid]);
    if (!missing.length) return;
    let cancelled = false;
    (async () => {
      const next = {};
      await Promise.all(missing.map(async (fid) => {
        try {
          const { data } = await api.get(`/files/${fid}`);
          next[fid] = { original_filename: data.original_filename, size: data.size };
        } catch { next[fid] = { original_filename: null }; }
      }));
      if (!cancelled) setAssetMeta((prev) => ({ ...prev, ...next }));
    })();
    return () => { cancelled = true; };
  }, [job?.asset_ids]);

  const assetLabel = (fid) => assetMeta[fid]?.original_filename || `asset-${fid.slice(0, 8)}`;

  const downloadAllAssetsZip = async () => {
    setDownloadingZip(true);
    try {
      const r = await api.get(`/admin/jobs/${id}/assets.zip`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${job.job_number || "job"}-assets.zip`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success(`Downloaded ${job.asset_ids.length} asset${job.asset_ids.length === 1 ? "" : "s"} as ZIP`);
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to download ZIP");
    } finally {
      setDownloadingZip(false);
    }
  };

  if (!job) return <Layout><div className="p-10 tech-label">LOADING…</div></Layout>;

  const iteration = job.designs?.find((d) => d.id === selectedIter);

  const postQuote = async () => {
    if (!quoteUrl.trim()) return toast.error("Quote URL required");
    if (job.betterproposals_url) {
      if (!bpEdited) return toast.error("Tick 'Proposal Edited in Better Proposals' to confirm you've added the AI sections in BP.");
      if (!/[?&]ContactID=/.test(quoteUrl)) {
        return toast.error("Quote URL is missing &ContactID=… — the proposal must be sent in BP first (that's what generates the client-specific URL). Send it in BP, then untick and re-tick the checkbox to pull the correct URL.");
      }
    }
    try {
      await api.post(`/jobs/${id}/quote`, { quote_url: quoteUrl, notes: quoteNotes, bp_proposal_edited: bpEdited });
      toast.success("Quote posted — customer notified"); load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to post quote");
    }
  };
  const acceptQuote = async () => { await api.post(`/jobs/${id}/quote/accept`); toast.success("Quote accepted → Design"); load(); };
  const uploadDesigns = async () => {
    if (!designFiles.length) return toast.error("Upload at least one file");
    await api.post(`/jobs/${id}/designs`, { file_ids: designFiles.map((f) => f.id), notes: designNotes });
    setDesignFiles([]); setDesignNotes("");
    toast.success("Iteration uploaded"); load();
  };
  const markDesignComplete = async (iterId) => { await api.post(`/jobs/${id}/designs/${iterId}/complete`); toast.success("Marked complete → Review · customer notified"); load(); };
  const deleteIteration = async (iter) => {
    const label = `V.${String(iter.iteration_number).padStart(2, '0')}`;
    if (!window.confirm(`Delete design iteration ${label}?\n\nThis will hide it from the customer. You can restore it later. Approved iterations cannot be deleted.`)) return;
    try {
      await api.delete(`/admin/jobs/${id}/design-iterations/${iter.id}`);
      toast.success(`Iteration ${label} deleted`);
      await load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to delete iteration");
    }
  };
  const restoreIteration = async (iter) => {
    const label = `V.${String(iter.iteration_number).padStart(2, '0')}`;
    try {
      await api.post(`/admin/jobs/${id}/design-iterations/${iter.id}/restore`);
      toast.success(`Iteration ${label} restored`);
      await load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to restore iteration");
    }
  };
  const uploadFinalProof = async () => {
    if (!finalProofFiles.length) return toast.error("Upload the final proof");
    await api.post(`/jobs/${id}/final-proof`, { file_id: finalProofFiles[0].id });
    setFinalProofFiles([]); toast.success("Final proof posted — customer notified"); load();
  };
  const markProductionComplete = async () => { await api.post(`/jobs/${id}/production-complete`); toast.success("Production complete → Shipping/Go Live"); load(); };
  const setShipping = async () => { await api.post(`/jobs/${id}/shipping`, { tracking_url: tracking, live_url: liveUrl, notes: shipNotes }); toast.success("Shipping details posted"); load(); };
  const sendMessage = async () => {
    if (!msgText.trim() && msgAssets.length === 0) return;
    await api.post(`/jobs/${id}/messages`, { text: msgText, asset_ids: msgAssets.map((a) => a.id) });
    setMsgText(""); setMsgAssets([]);
    load();
  };
  const forceComplete = async () => { await api.post(`/jobs/${id}/complete`); toast.success("Marked completed"); load(); };
  const closeProject = async () => {
    if (job.phase === "Completed") return;
    const note = window.prompt(`Close project "${job.project_name}"?\n\nAdd an optional closing note (visible in the project activity log). Press Cancel to abort.`, "");
    if (note === null) return;
    try {
      await api.post(`/jobs/${id}/complete`, { note });
      toast.success("Project closed");
      await load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to close project");
    }
  };
  const reopenProject = async () => {
    if (job.phase !== "Completed") return;
    const note = window.prompt(`Reopen "${job.project_name}"?\n\nOptional reopen note. Press Cancel to abort.`, "");
    if (note === null) return;
    try {
      const { data } = await api.post(`/jobs/${id}/reopen`, { note });
      toast.success(`Project reopened → ${data.phase}`);
      await load();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to reopen project");
    }
  };
  const deleteProject = async () => {
    const confirm = window.prompt(`PERMANENTLY DELETE "${job.project_name}" (${job.job_number})?\n\nThis cannot be undone. All comments and job data will be removed forever.\n\nType DELETE to confirm.`, "");
    if (confirm !== "DELETE") return;
    try {
      await api.delete(`/admin/jobs/${id}`);
      toast.success(`Project ${job.job_number} deleted`);
      window.location.href = "/admin";
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Failed to delete project");
    }
  };

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 py-8 space-y-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="chip chip-navy mb-3">Admin · {job.job_number}</div>
            <h1 className="font-display text-4xl md:text-5xl">{job.project_name}</h1>
            <div className="text-[var(--gc-text-muted)] mt-1">{job.project_type} · {job.customer?.first_name} {job.customer?.last_name} · {job.customer?.business_name}</div>
            <div className="text-xs text-[var(--gc-text-muted)] mt-1 font-mono-tech">{job.customer?.email} · {job.customer?.phone}</div>
            {job.phase === "Completed" && (job.completed_at || job.completed_by_name) && (
              <div className="text-xs text-[var(--gc-success)] mt-2 font-mono-tech" data-testid="closed-meta">
                Closed{job.completed_by_name ? ` by ${job.completed_by_name}` : ""}{job.completed_at ? ` · ${new Date(job.completed_at).toLocaleString()}` : ""}
              </div>
            )}
          </div>
          <div className="flex items-start gap-3 flex-wrap">
            {job.phase !== "Completed" ? (
              <button
                onClick={closeProject}
                className="btn-primary inline-flex items-center gap-2 px-4 py-3 text-sm bg-[var(--gc-success)] border-[var(--gc-success)] hover:opacity-90"
                data-testid="close-project-btn"
                title="Mark this project as Completed"
              >
                <CheckCircle size={16} weight="bold"/> Close Project
              </button>
            ) : (
              <button
                onClick={reopenProject}
                className="btn-secondary inline-flex items-center gap-2 px-4 py-3 text-sm"
                data-testid="reopen-project-btn"
                title="Reopen this closed project"
              >
                <ArrowCounterClockwise size={16} weight="bold"/> Reopen
              </button>
            )}
            <button
              onClick={deleteProject}
              className="btn-secondary inline-flex items-center gap-2 px-4 py-3 text-sm text-[var(--gc-danger)] border-[var(--gc-danger)] hover:bg-red-50"
              data-testid="delete-project-btn"
              title="Permanently delete this project"
            >
              <Trash size={16} weight="bold"/> Delete
            </button>
            <div className="sticker sticker-orange px-6 py-4">
              <div className="tech-label">Phase</div>
              <div className="font-display text-2xl text-[var(--gc-orange)]">{job.phase}</div>
            </div>
          </div>
        </div>

        <div className="pt-4">
          <JobTimeline
            currentPhase={job.phase}
            hadRevisions={(job.review_history || []).some(h => h.decision === "revisions")}
            hadRejection={(job.final_proof_history || []).some(h => h.approval?.status === "not_approved")}
            showHelp={false}
          />
        </div>

        {(job.activity_log || []).length > 0 && (
          <details className="sticker p-4" data-testid="activity-log">
            <summary className="cursor-pointer tech-label text-[var(--gc-orange)] font-bold">
              Project activity ({job.activity_log.length})
            </summary>
            <ol className="mt-3 space-y-2 text-sm">
              {job.activity_log.slice().reverse().map((e) => (
                <li key={e.id} className="border-l-4 border-[var(--gc-navy)] pl-3" data-testid={`activity-${e.id}`}>
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="font-semibold text-[var(--gc-navy)]">
                      {e.kind === "closed" ? "Project closed" : e.kind === "reopened" ? `Project reopened → ${e.to_phase}` : e.kind}
                    </div>
                    <div className="tech-label">{new Date(e.at).toLocaleString()}</div>
                  </div>
                  <div className="text-xs text-[var(--gc-text-muted)] font-mono-tech">by {e.by_name}{e.from_phase ? ` · from ${e.from_phase}` : ""}</div>
                  {e.note && <div className="mt-1 whitespace-pre-wrap text-[var(--gc-navy)]">“{e.note}”</div>}
                </li>
              ))}
            </ol>
          </details>
        )}

        <section className="sticker p-6">
          <div className="chip mb-3">// onboarding data</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="tech-label">Products ({job.products?.length || 0})</div>
            </div>
            <div>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div className="tech-label">Assets ({job.asset_ids?.length || 0})</div>
                {job.asset_ids?.length > 0 && (
                  <button
                    onClick={downloadAllAssetsZip}
                    disabled={downloadingZip}
                    className="btn-secondary inline-flex items-center gap-1 text-xs px-3 py-1.5 disabled:opacity-50"
                    data-testid="download-all-assets-zip-btn"
                    title="Download all assets as a ZIP"
                  >
                    <DownloadSimple size={13} weight="bold"/>
                    {downloadingZip ? "Zipping…" : "Download all (ZIP)"}
                  </button>
                )}
              </div>
              <div className="space-y-1 mt-2">
                {job.asset_ids?.map((fid) => (
                  <a key={fid} href="#" onClick={async (e) => {
                    e.preventDefault();
                    const r = await api.get(`/files/${fid}/download`, { responseType: "blob" });
                    const url = URL.createObjectURL(r.data);
                    const a = document.createElement("a");
                    a.href = url; a.download = assetLabel(fid);
                    document.body.appendChild(a); a.click(); document.body.removeChild(a);
                    setTimeout(() => URL.revokeObjectURL(url), 1000);
                  }} className="text-sm text-[var(--gc-orange)] font-semibold hover:underline block break-all" data-testid={`asset-${fid}`}>
                    {assetLabel(fid)}
                  </a>
                ))}
              </div>
            </div>
          </div>
          {job.questionnaire && (
            <details className="mt-4">
              <summary className="cursor-pointer tech-label text-[var(--gc-orange)] font-bold">View questionnaire</summary>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(job.questionnaire).map(([k, v]) => (
                  <div key={k} className="text-sm">
                    <div className="tech-label">{k.replace(/_/g, ' ')}</div>
                    <div className="text-[var(--gc-navy)]">{v || "—"}</div>
                  </div>
                ))}
              </div>
            </details>
          )}
        </section>

        <QuoteDraftPanel jobId={id} initialHtml={job.quote_draft || ""} initialBpUrl={job.betterproposals_url || ""} onSaved={load} />


        <section className="sticker p-6" data-testid="job-quote-stage-wrapper">
          <div className="chip mb-3">// quote stage</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <div className="tech-label mb-2">Quote URL</div>
              <input value={quoteUrl} onChange={(e) => setQuoteUrl(e.target.value)} placeholder="https://your-proposal-url" className="gc-input" data-testid="quote-url-input" />
            </div>
            <div>
              <div className="tech-label mb-2">Notes (optional)</div>
              <input value={quoteNotes} onChange={(e) => setQuoteNotes(e.target.value)} className="gc-input" data-testid="quote-notes-input" />
            </div>
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            <button
              onClick={postQuote}
              disabled={
                (job.betterproposals_url && !bpEdited) ||
                (job.betterproposals_url && !!quoteUrl && !/[?&]ContactID=/.test(quoteUrl))
              }
              className="btn-primary px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="post-quote-btn"
            >
              {job.quote ? "Update Quote" : "Post Quote"}
            </button>
            {job.quote && job.quote.status !== "accepted" && (
              <button onClick={acceptQuote} className="btn-secondary px-4 py-2 text-sm text-[var(--gc-success)]" data-testid="accept-quote-btn">
                Mark Accepted → Design
              </button>
            )}
          </div>
          {job.betterproposals_url && (
            <label className="mt-4 flex items-start gap-3 cursor-pointer bg-[var(--gc-cream,#fdf8f0)] border-2 border-[var(--gc-navy)] px-4 py-3" data-testid="bp-edited-checkbox-wrap">
              <input
                type="checkbox"
                checked={bpEdited}
                onChange={async (e) => {
                  const next = e.target.checked;
                  setBpEdited(next);
                  if (next) {
                    try {
                      const { data } = await api.post(`/admin/jobs/${id}/refresh-bp-url`);
                      if (data.url) {
                        await load();
                        setBpEdited(true);
                        setQuoteUrl(data.url);
                        if (data.has_contact_link) {
                          toast.success("Client URL fetched (with ContactID) ✓");
                        } else {
                          toast.warning("Proposal hasn't been sent in BP yet — URL doesn't have a ContactID. Send it in BP, then tick this box again.");
                        }
                      } else {
                        toast.error("BetterProposals didn't return a URL yet — try again after sending the proposal in BP.");
                      }
                    } catch (ex) {
                      toast.error(ex.response?.data?.detail || "Couldn't fetch the BP URL");
                    }
                  }
                }}
                className="mt-1 h-5 w-5 accent-[var(--gc-orange)]"
                data-testid="bp-edited-checkbox"
              />
              <span className="text-sm leading-snug">
                <strong className="font-semibold">Proposal Edited in Better Proposals</strong>
                <span className="block text-[var(--gc-text-muted)]">
                  Required — confirms you&apos;ve pasted the Introduction, Project Goals, and Design &amp; Branding sections into the BP proposal before sending. Ticking this pulls the latest proposal URL from BP (with the client&apos;s ContactID) into the Quote URL field above.
                </span>
              </span>
            </label>
          )}
        </section>

        {(["Design", "Review", "Final Proof Sign-Off", "Production", "Shipping/Go Live", "Completed"].includes(job.phase)) && (
          <section className="sticker p-6">
            <div className="chip mb-3">// design gallery</div>
            {job.designs?.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-6">
                <div className="space-y-2">
                  {job.designs.map((d) => {
                    const isDeleted = !!d.is_deleted;
                    const canDelete = !isDeleted && d.status !== "approved";
                    return (
                      <div key={d.id}
                        className={`w-full text-left p-3 border-2 transition-colors ${isDeleted ? 'border-dashed border-[var(--gc-border)] bg-[var(--gc-bg-alt)] opacity-70' : selectedIter === d.id ? 'border-[var(--gc-orange)] bg-[var(--gc-orange-tint)]' : 'border-[var(--gc-navy)] bg-white hover:bg-[var(--gc-bg-alt)]'}`}
                        data-testid={`admin-iter-${d.id}`}>
                        <button onClick={() => setSelectedIter(d.id)} className="w-full text-left" data-testid={`admin-iter-select-${d.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="font-mono-tech text-xs text-[var(--gc-text-muted)]">V.{String(d.iteration_number).padStart(2, '0')}</div>
                            {isDeleted && <span className="chip" style={{background:'#fee2e2', color:'#991B1B', borderColor:'#fecaca'}} data-testid={`admin-iter-deleted-badge-${d.id}`}>Deleted</span>}
                          </div>
                          <div className="text-sm font-semibold mt-1">{d.files?.length || 0} file(s)</div>
                          <div className="tech-label mt-1">{d.status.replace(/_/g, ' ')}</div>
                        </button>
                        {!isDeleted && d.status === "in_progress" && (
                          <button onClick={(e) => { e.stopPropagation(); markDesignComplete(d.id); }}
                            className="btn-primary mt-2 w-full text-xs px-2 py-1.5"
                            data-testid={`mark-complete-${d.id}`}>
                            Mark Complete
                          </button>
                        )}
                        <div className="mt-2 flex gap-1">
                          {canDelete && (
                            <button onClick={(e) => { e.stopPropagation(); deleteIteration(d); }}
                              className="btn-secondary flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5 text-[var(--gc-danger)] border-[var(--gc-danger)] hover:bg-red-50"
                              data-testid={`delete-iter-${d.id}`}
                              title="Delete iteration (soft-delete, restorable)">
                              <Trash size={12} weight="bold"/> Delete
                            </button>
                          )}
                          {isDeleted && (
                            <button onClick={(e) => { e.stopPropagation(); restoreIteration(d); }}
                              className="btn-secondary flex-1 inline-flex items-center justify-center gap-1 text-[11px] px-2 py-1.5"
                              data-testid={`restore-iter-${d.id}`}>
                              <ArrowCounterClockwise size={12} weight="bold"/> Restore
                            </button>
                          )}
                          {!isDeleted && d.status === "approved" && (
                            <div className="flex-1 text-[10px] text-[var(--gc-text-muted)] italic px-1 py-1.5 text-center">
                              Approved — locked
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div className="space-y-6">
                  {iteration?.is_deleted && (
                    <div className="border-l-4 border-[var(--gc-danger)] bg-red-50 p-4 text-sm" data-testid="admin-iter-deleted-notice">
                      <strong className="text-[var(--gc-danger)]">This iteration is deleted</strong> — hidden from the customer. Use <em>Restore</em> to bring it back.
                    </div>
                  )}
                  {iteration?.files?.map((f) => (
                    <DesignFileViewer key={f.id} jobId={job.id} job={job} design={iteration} file={f} onCommentAdded={load} />
                  ))}
                </div>
              </div>
            )}

            {(["Design", "Review"].includes(job.phase)) && (
              <AdditionalAssetsPanel
                jobId={job.id}
                onAdded={load}
                title="Upload additional client / project assets"
              />
            )}

            {(["Design", "Review"].includes(job.phase)) && (
              <div className="mt-6 border-t-2 border-[var(--gc-navy)] pt-6 space-y-3">
                <div className="chip chip-orange">// new iteration</div>
                <AssetUploader value={designFiles} onChange={setDesignFiles} label="Design files (images or PDFs)" testid="design-upload" />
                <textarea value={designNotes} onChange={(e) => setDesignNotes(e.target.value)} placeholder="Iteration notes (optional)…" rows={2} className="gc-input" data-testid="design-notes-input" />
                <button onClick={uploadDesigns} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="upload-designs-btn">
                  <UploadSimple size={16} weight="bold"/> Upload Iteration
                </button>
              </div>
            )}
          </section>
        )}

        {(["Final Proof Sign-Off", "Production", "Shipping/Go Live", "Completed"].includes(job.phase)) && (
          <section className="sticker p-6">
            <div className="chip chip-orange mb-3">// final proof</div>
            {job.final_proof ? (
              <div>
                <DesignFileViewer jobId={job.id} job={job} design={{ id: "final-proof" }} file={{ id: "fp", file_id: job.final_proof.file_id, design_number: "FINAL PROOF" }} readOnly={!!job.approval} onCommentAdded={load} />
                {job.approval && (
                  <div className={`mt-4 border-l-4 p-4 ${job.approval.status === 'approved' ? 'border-[var(--gc-success)] bg-green-50' : job.approval.status === 'approved_with_amendments' ? 'border-[var(--gc-warning)] bg-yellow-50' : 'border-[var(--gc-danger)] bg-red-50'}`}>
                    <div className={`flex items-center gap-2 font-display text-lg ${job.approval.status === 'approved' ? 'text-[var(--gc-success)]' : job.approval.status === 'approved_with_amendments' ? 'text-[var(--gc-warning)]' : 'text-[var(--gc-danger)]'}`}>
                      <CheckCircle size={22} weight="fill"/> {job.approval.status === 'not_approved' ? 'Not Approved' : job.approval.status === 'approved_with_amendments' ? 'Approved with Amendments' : 'Approved'}
                    </div>
                    <div className="text-xs text-[var(--gc-text-muted)] mt-1">Signed by {job.approval.signatory_name} · {new Date(job.approval.signed_at).toLocaleString()}</div>
                    {job.approval.comments && <div className="mt-2 text-sm text-[var(--gc-navy)]"><strong>Reason:</strong> {job.approval.comments}</div>}
                    {job.approval.signature_data_url && <img src={job.approval.signature_data_url} alt="signature" className="mt-3 max-h-24 bg-white p-2 border border-[var(--gc-navy)]" />}
                  </div>
                )}
                {/* Allow re-uploading final proof after rejection */}
                {job.approval?.status === "not_approved" && (
                  <div className="mt-6 border-t border-[var(--gc-border)] pt-6 space-y-3">
                    <div className="chip chip-orange">// upload new final proof</div>
                    <AssetUploader value={finalProofFiles} onChange={setFinalProofFiles} label="New final proof (PDF/image)" testid="final-proof-reupload" />
                    <button onClick={uploadFinalProof} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="reupload-final-proof-btn">
                      <UploadSimple size={16} weight="bold"/> Post New Final Proof
                    </button>
                    <div className="text-xs text-[var(--gc-text-muted)]">The current rejected proof will be moved to history.</div>
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-3">
                <AssetUploader value={finalProofFiles} onChange={setFinalProofFiles} label="Upload final proof (PDF/image)" testid="final-proof-upload" />
                <button onClick={uploadFinalProof} className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm" data-testid="upload-final-proof-btn">
                  <UploadSimple size={16} weight="bold"/> Post Final Proof
                </button>
              </div>
            )}

            {/* Previous rejected/superseded final proofs */}
            {job.final_proof_history?.length > 0 && (
              <div className="mt-8">
                <div className="chip mb-3">// previous final proofs</div>
                <div className="space-y-2">
                  {job.final_proof_history.slice().reverse().map((fp, idx) => {
                    const revNum = job.final_proof_history.length - idx;
                    const status = fp.approval?.status;
                    const statusLabel = status === "not_approved" ? "Rejected" : status === "approved_with_amendments" ? "Amendments" : "Superseded";
                    return (
                      <details key={idx} className="border border-[var(--gc-border)]" data-testid={`admin-prev-proof-${idx}`}>
                        <summary className="cursor-pointer p-3 flex items-center justify-between hover:bg-[var(--gc-bg-alt)]">
                          <span className="flex items-center gap-3 text-sm">
                            <span className="tech-label">V.{String(revNum).padStart(2, '0')}</span>
                            <span className="font-semibold">{new Date(fp.uploaded_at).toLocaleDateString()}</span>
                            <span className="chip">{statusLabel}</span>
                          </span>
                        </summary>
                        <div className="border-t border-[var(--gc-border)] p-3 bg-[var(--gc-bg-alt)]">
                          <DesignFileViewer jobId={job.id} job={job} design={{ id: `prev-fp-${idx}` }} file={{ id: `pfp-${idx}`, file_id: fp.file_id, design_number: `V.${String(revNum).padStart(2, '0')}` }} readOnly />
                          {fp.approval && (
                            <div className="mt-3 text-sm">
                              <div className="tech-label">Reason</div>
                              <div className="mt-1">{fp.approval.comments || "—"}</div>
                              <div className="tech-label mt-2">Signed by</div>
                              <div className="mt-1 text-xs">{fp.approval.signatory_name} · {new Date(fp.approval.signed_at).toLocaleString()}</div>
                            </div>
                          )}
                        </div>
                      </details>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {(["Production", "Shipping/Go Live", "Completed"].includes(job.phase)) && (
          <section className="sticker p-6">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="chip">// production</div>
              {job.phase === "Production" && (
                <button onClick={markProductionComplete} className="btn-primary px-4 py-2 text-sm" data-testid="production-complete-btn">
                  Mark Production Complete → Shipping/Go Live
                </button>
              )}
            </div>
            <div className="mt-4 space-y-2 max-h-[400px] overflow-auto">
              {(job.messages || []).map((m) => (
                <div key={m.id} className={`p-3 border-2 ${m.author_role === "admin" ? 'border-[var(--gc-orange)] bg-[var(--gc-orange-tint)]' : 'border-[var(--gc-navy)] bg-white'}`} data-testid={`admin-message-${m.id}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="text-sm font-semibold">{m.author_name} <span className="tech-label ml-1">{(m.author_role || '').toUpperCase()}</span></div>
                    <div className="tech-label">{new Date(m.created_at).toLocaleString()}</div>
                  </div>
                  {m.text && <div className="text-sm whitespace-pre-wrap">{m.text}</div>}
                  {(m.asset_ids || []).length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`admin-message-attachments-${m.id}`}>
                      {m.asset_ids.map((fid) => <MessageAttachment key={fid} fileId={fid} />)}
                    </div>
                  )}
                </div>
              ))}
              {(!job.messages || job.messages.length === 0) && <div className="text-[var(--gc-text-muted)] text-sm">No messages yet.</div>}
            </div>
            <div className="mt-3 space-y-2">
              <AssetUploader value={msgAssets} onChange={setMsgAssets} label="Attach files (optional)" testid="admin-message-assets" />
              <textarea value={msgText} onChange={(e) => setMsgText(e.target.value)} rows={2} placeholder="Send an update to the customer…" className="gc-input" data-testid="admin-message-input" />
              <button onClick={sendMessage} disabled={!msgText.trim() && msgAssets.length === 0}
                className="btn-primary inline-flex items-center gap-2 px-4 py-2 text-sm disabled:opacity-50"
                data-testid="admin-send-message-btn">
                <PaperPlaneRight size={16} weight="bold"/> Send
              </button>
            </div>
          </section>
        )}

        {(["Shipping/Go Live", "Completed"].includes(job.phase)) && (
          <section className="sticker p-6">
            <div className="chip chip-orange mb-3">// shipping / go live</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="tech-label mb-2 flex items-center gap-1"><Truck size={12} weight="bold"/> Tracking URL</div>
                <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="https://tracker…" className="gc-input" data-testid="tracking-input" />
              </div>
              <div>
                <div className="tech-label mb-2 flex items-center gap-1"><Globe size={12} weight="bold"/> Live URL</div>
                <input value={liveUrl} onChange={(e) => setLiveUrl(e.target.value)} placeholder="https://live-site…" className="gc-input" data-testid="live-url-input" />
              </div>
            </div>
            <textarea value={shipNotes} onChange={(e) => setShipNotes(e.target.value)} rows={2} placeholder="Notes (optional)…" className="gc-input mt-3" data-testid="ship-notes" />
            <div className="mt-3 flex gap-2 flex-wrap">
              <button onClick={setShipping} className="btn-primary px-4 py-2 text-sm" data-testid="post-shipping-btn">
                Post Shipping/Go Live
              </button>
              {job.phase !== "Completed" && (
                <button onClick={closeProject} className="btn-secondary px-4 py-2 text-sm" data-testid="force-complete-btn">
                  Mark Completed Now
                </button>
              )}
            </div>
          </section>
        )}
      </div>
    </Layout>
  );
}
